import { useSelector } from 'react-redux';
import { ticApi } from 'redux/modules/tic/api-slice/tic.apiSlice';
import { getUseTicAPI } from 'redux/modules/tic/tic.auth.selectors';
import { getMemoryQueryParams } from 'redux/modules/tic/tic.memories.selectors';
import { getIsSubscriptionExpiredOrTrialEnded } from 'redux/selectors/accounts';
import useTicAiDataSubscriber from './useTicAiDataSubscriber';

/**
 * Subscribes to the relevant Memories data for the TIC,
 * loading filters, toolConfigs, and finally, memories.
 *
 * @returns void See selector usage for how this data flows back to UI.
 */
const useTicMemoriesDataSubscriber = (): void => {
  // skip (aka don't load data) if TIC API is not available/authenticated
  const canUseTicAPI = useSelector(getUseTicAPI);
  const memoryQueryParams = useSelector(getMemoryQueryParams);
  const subscriptionExpired = useSelector(getIsSubscriptionExpiredOrTrialEnded);
  const skip = !canUseTicAPI || subscriptionExpired;

  // If we need Memory Tracker Sessions, uncomment `getMemoryTrackerSessions` in `openapi-tic-rtkquery-config.cjs`
  // ticApi.useGetMemoryTrackerSessionsQuery(undefined, { skip, pollingInterval: 60 * 60 * 1000 });

  // Load global & per-tool view preferences (these merge into `MemoryConstructionParams`, ref below)
  ticApi.useGetMemoryConfigQuery(undefined, { skip });
  ticApi.useGetToolConfigsQuery(undefined, { skip });

  // Fetch Memories
  // NB: If we remove tool/filter loading on this API endpoint,
  // we'll need to delay loading until they're loaded (above)
  ticApi.useGetMemoriesQuery(memoryQueryParams, { skip });

  // Subscribe to TIC AI data, AI pusher signals, and get data back via selectors
  // DELAY loading until we have memories, so we can auto-trigger AI actions in status callback
  useTicAiDataSubscriber({ skip });
};

export default useTicMemoriesDataSubscriber;
